import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// Below `sm`, dialogs behave like a native bottom sheet — anchored to
// the bottom edge, full-width, rounded top corners, with a drag handle
// — instead of a centered desktop-style modal. Above `sm`, unchanged.
//
// The class ORDER below is load-bearing, not stylistic. cn() runs every
// className through tailwind-merge, which treats `left-*`/`top-*` and
// `inset-x-*`/`inset-y-*` as the SAME conflict group (inset-x sets both
// left and right) and keeps only the LAST one written per group at a
// given modifier — it doesn't defer to the browser's normal CSS cascade
// at all, it deletes the loser from the className string outright.
// `sm:inset-x-auto`/`sm:bottom-auto` are written BEFORE `sm:left-[50%]`/
// `sm:top-[50%]` for exactly this reason: written the other way round
// (as this file previously had it), tailwind-merge silently drops
// `sm:left-[50%]` from the output entirely — confirmed by literally
// running this file's own cn() against both orderings. With `left`
// deleted, the box's position is `left:auto` (i.e. wherever a
// position:fixed element's static position happens to land), and
// sm:translate-x-[-50%] still fires on top of that undefined starting
// point — which is what was actually cutting the modal off on the left,
// not a plain cascade-order issue the way an earlier pass at this fix
// assumed. sm:w-auto (below) is separately still required — the mobile
// base sets w-full for the bottom sheet, and without an override the
// box stays 100vw wide at every screen size regardless of the
// left/inset ordering fix.
const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Mobile: bottom sheet
        "fixed inset-x-0 bottom-0 z-50 grid w-full gap-4 border-t bg-background p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] shadow-lg duration-250 max-h-[85dvh] overflow-y-auto rounded-t-3xl " +
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 " +
        "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom " +
        // Desktop (sm+): centered modal. inset-x-auto/bottom-auto MUST come
        // before left-[50%]/top-[50%] — see the comment above.
        "sm:inset-x-auto sm:bottom-auto sm:top-[50%] sm:left-[50%] sm:w-auto sm:max-w-lg sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-lg sm:border sm:max-h-[90dvh] " +
        "sm:data-[state=closed]:slide-out-to-left-1/2 sm:data-[state=closed]:slide-out-to-top-[48%] sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=closed]:zoom-out-95 " +
        "sm:data-[state=open]:slide-in-from-left-1/2 sm:data-[state=open]:slide-in-from-top-[48%] sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=open]:zoom-in-95",
        className
      )}
      {...props}>
      {/* Drag handle — mobile bottom-sheet affordance only */}
      <div className="sm:hidden mx-auto -mt-2 mb-1 h-1.5 w-10 rounded-full bg-muted-foreground/25 shrink-0" aria-hidden="true" />
      {children}
      <DialogPrimitive.Close
        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
